import type { AlertApi, AlertMessage } from '@backstage/core-plugin-api';
import type { Observable } from '@backstage/types';

/** Suppress duplicate error toasts with the same message within this window. */
export const DEFAULT_ERROR_ALERT_DEBOUNCE_MS = 15_000;

/**
 * Wraps an {@link AlertApi} and debounces repeated error alerts with the same message.
 * Success/info/warning alerts are forwarded immediately.
 */
export class DebouncedAlertApi implements AlertApi {
  private readonly lastErrorPostAt = new Map<string, number>();

  constructor(
    private readonly inner: AlertApi,
    private readonly errorDebounceMs = DEFAULT_ERROR_ALERT_DEBOUNCE_MS,
  ) {}

  post(alert: AlertMessage): void {
    if (alert.severity === 'error') {
      const key = alert.message;
      const now = Date.now();
      const last = this.lastErrorPostAt.get(key);
      if (last !== undefined && now - last < this.errorDebounceMs) {
        return;
      }
      this.lastErrorPostAt.set(key, now);
    }

    this.inner.post(alert);
  }

  alert$(): Observable<AlertMessage> {
    return this.inner.alert$();
  }
}
